import React from "react";
import { Target, Workflow, Shield, BarChart, Layers, Copy, XCircle, Zap, ArrowRight, TrendingUp, CheckCircle } from "lucide-react";

const Solution = () => {
  const solutionFeatures = [
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Autonomous Remediation",
      description: "AI agents fix vulnerabilities at cloud, code, and config level"
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Unified View",
      description: "Everything in one place - all security issues across all tools"
    },
    {
      icon: <Copy className="w-6 h-6" />,
      title: "De-duplication",
      description: "Removes noise by combining duplicate findings automatically"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Risk-Based Triage",
      description: "Prioritizes by exploitability and business impact, not just CVSS"
    },
    {
      icon: <Workflow className="w-6 h-6" />,
      title: "Auto-Ticketing",
      description: "Creates, assigns, and tracks issues in Jira or ServiceNow"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Accountability",
      description: "Clear ownership with SLA tracking and escalation"
    },
    {
      icon: <XCircle className="w-6 h-6" />,
      title: "False Positive Detection",
      description: "AI identifies and filters out non-exploitable findings"
    },
    {
      icon: <BarChart className="w-6 h-6" />,
      title: "ROI Analytics",
      description: "Tool performance and team efficiency metrics for leadership"
    }
  ];

  return (
    <section id="solution" className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Autonomous Intelligence
            <span className="text-blue-600 block">That Actually Works</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            A self-learning security agent that integrates across all tools and environments to deliver end-to-end vulnerability management
          </p>
        </div>

        {/* Solution Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {solutionFeatures.map((feature, index) => (
            <div 
              key={index}
              className="bg-white border border-gray-100 rounded-xl p-5 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-3">
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* From Chaos to Control Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            From Chaos to
            <span className="text-blue-600"> Control</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 mx-auto mb-3">
                <ArrowRight className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Speed</h4>
              <p className="text-gray-600">Faster remediation, fewer bottlenecks</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 mx-auto mb-4">
                <TrendingUp className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Efficiency</h4>
              <p className="text-gray-600">Eliminate duplicates & false positives</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 mx-auto mb-4">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Protection</h4>
              <p className="text-gray-600">Critical issues fixed before attackers strike</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solution;